import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useTheme } from '@mui/material';
import { Row } from './ds';
import { MaskedCurrency } from './MaskedValue';
import { resolveAmountColor } from './AmountDisplay';
import { InlineLoader } from './LoadingIndicator';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { interpolate } from '../utils/entryType';

// The payee name the archive handlers stamp on every closing line (#2157).
// It is stored data, not copy, so it is never translated.
const ARCHIVAL_TRUE_UP_PAYEE = 'Archival True-Up';

/**
 * Archive confirmation (#2159, epic #2201 Rules 12-15): one dialog for
 * budgets and ledgers. Says what archiving does to this item, then draws the
 * item before and after with the page's own Row at its real balance: after,
 * it reads $0.00 under its closing line, or with no line when the balance is
 * already $0.00 (Rule 12). The caller owns the DELETE call and the
 * submitting flag.
 *
 * @param {object} props
 * @param {'budget'|'ledger'} props.kind
 * @param {object|null} props.item         the budget or ledger; null keeps the dialog closed
 * @param {number} props.balance           its balance in dollars, as its row shows it
 * @param {React.ReactNode} props.icon     its row's icon element
 * @param {string} props.category          its row's icon-box category
 * @param {boolean} props.submitting       the DELETE is in flight
 * @param {function} props.onCancel        Cancel, Escape or the backdrop
 * @param {function} props.onConfirm       Archive
 */
const ArchiveDialog = ({ kind, item, balance, icon, category, submitting, onCancel, onConfirm }) => {
  const strings = useEngineStrings();
  const { ds, palette } = useTheme();
  const open = item != null;
  const name = open ? item.name : '';
  const dollars = typeof balance === 'number' ? balance : 0;
  const hasBalance = Math.round(dollars * 100) !== 0;

  const amount = (value) => {
    const cents = Math.round(value * 100);
    return <MaskedCurrency value={value} style={{ color: resolveAmountColor(palette, 'row', cents < 0, cents === 0) }} />;
  };

  const points = [
    strings[hasBalance ? 'archive.trueUp' : 'archive.noTrueUp'],
    strings[kind === 'ledger' ? 'archive.linksLedger' : 'archive.linksBudget'],
    strings['archive.leavesTotals'],
    ...(kind === 'ledger' ? [strings['archive.linesKeepCounting']] : []),
    strings['archive.restore'],
  ];

  const labelSx = {
    fontSize: '11px',
    color: palette.text.dim,
    letterSpacing: ds.font.letterUpper,
    textTransform: 'uppercase',
    mb: `${ds.space[2]}px`,
  };
  const panelSx = {
    border: `1px solid ${palette.divider}`,
    borderRadius: `${ds.radius.container}px`,
    px: `${ds.space[6]}px`,
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="sm" fullWidth scroll="paper" data-testid="archive-dialog">
      <DialogTitle>{interpolate(strings['archive.title'], { name })}</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: `${ds.space[3]}px` }}>
          {strings['archive.balanceNow']} {amount(dollars)}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: `${ds.space[8]}px` }}>
          {points.map((point) => (
            <Typography key={point} component="li" sx={{ fontSize: `${ds.font.row}px`, mb: `${ds.space[2]}px` }}>
              {point}
            </Typography>
          ))}
        </Box>
        <Box role="group" aria-label={strings['archive.drawingAria']} sx={{ mt: `${ds.space[7]}px` }}>
          <Typography sx={labelSx}>{strings['archive.before']}</Typography>
          <Box sx={{ ...panelSx, mb: `${ds.space[6]}px` }}>
            <Row icon={icon} category={category} name={name} value={amount(dollars)} divider={false} />
          </Box>
          <Typography sx={labelSx}>{strings['archive.after']}</Typography>
          <Box sx={panelSx}>
            <Row icon={icon} category={category} archived name={name} meta={strings['archive.archivedMeta']} value={amount(0)} divider={false} />
            {hasBalance && (
              <Box
                data-testid="archive-true-up-line"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: `${ds.space[3]}px`,
                  pl: `${ds.size.iconBox + 11}px`,
                  pb: '9px',
                  fontSize: `${ds.font.meta}px`,
                  color: palette.text.secondary,
                }}
              >
                <span>{ARCHIVAL_TRUE_UP_PAYEE}</span>
                {amount(-dollars)}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>{strings['archive.cancel']}</Button>
        <Button onClick={onConfirm} variant="contained" color="warning" disabled={submitting}>
          {submitting ? <InlineLoader message={strings['archive.archiving']} size={16} /> : strings['archive.confirm']}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArchiveDialog;
